/**
 * 角色卡列表视图（T07）。
 * -----------------------------------------------------------------------------
 * 列出 `presetRepo` 中全部角色卡（头像 / 名称 / 简介 / 标签），提供
 * 新建 / 编辑 / 复制 / 导出 / 删除（二次确认）与导入入口。
 * 所有可见部件通过 `Framed.tsx` 登记进组件清单（390x844 逻辑坐标）。
 * ============================================================================= */

import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import type { ReactElement } from 'react';

import { useMotionPresets } from '../../design/motion';
import type { Preset } from '../../core/types';
import { presetLocalLayout } from './componentMeta';
import { FramedBox, FramedButton, FramedHeading, FramedMotionBox, FramedText } from './Framed';
import { presetItemBoxes } from './listItemBoxes';
import {
  COMMUNITY_BUTTON_BOX,
  CREATE_BUTTON_BOX,
  IMPORT_CARD_BUTTON_BOX,
  IMPORT_WORLD_BOOK_BUTTON_BOX,
  LIST_BOX,
  LIST_HEADER_BOX,
  LIST_TITLE_BOX,
  NEW_CONTACT_MENU_BOX,
  PAGE_BOX,
} from './layout';

export interface PresetListViewProps {
  presets: Preset[];
  importError: string | null;
  pendingDelete: Preset | null;
  importing?: boolean;
  onCreate: () => void;
  onImportCardClick: () => void;
  onImportWorldBookClick: () => void;
  /** T22：进入「角色卡社区」独立页（#/community）。 */
  onOpenCommunity: () => void;
  onEdit: (preset: Preset) => void;
  onDuplicate: (preset: Preset) => void;
  onExport: (preset: Preset) => void;
  onRequestDelete: (preset: Preset) => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

// 本视图私有部件的坐标一律从 `componentMeta.ts` 的声明读取（不再内联一份，避免漂移）。
const ERROR_BOX = presetLocalLayout('presets.notice');
const EMPTY_BOX = presetLocalLayout('presets.empty');

const DIALOG_BACKDROP_BOX = presetLocalLayout('presets.deleteDialog.backdrop');
const DIALOG_PANEL_BOX = presetLocalLayout('presets.deleteDialog');
const DIALOG_MESSAGE_BOX = presetLocalLayout('presets.deleteDialog.message');
const DIALOG_CANCEL_BOX = presetLocalLayout('presets.deleteDialog.cancelButton');
const DIALOG_CONFIRM_BOX = presetLocalLayout('presets.deleteDialog.confirmButton');

/** 头像：图片地址渲染 img，emoji / 文字直接显示，为空时用名称首字。 */
function avatarContent(preset: Preset): ReactElement | string {
  const avatar = preset.avatar?.trim();
  if (avatar !== undefined && avatar !== '') {
    if (/^(https?:|data:)/.test(avatar)) {
      return <img className="zs-avatar-img" src={avatar} alt="" />;
    }
    return avatar;
  }
  return preset.name.trim().charAt(0) || '角';
}

/** 世界书条目数提示。 */
function worldBookSummary(preset: Preset): string {
  const count = preset.worldBook?.length ?? 0;
  return count === 0 ? '无世界书' : `世界书 ${count} 条`;
}

export function PresetListView({
  presets,
  importError,
  pendingDelete,
  importing = false,
  onCreate,
  onImportCardClick,
  onImportWorldBookClick,
  onOpenCommunity,
  onEdit,
  onDuplicate,
  onExport,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: PresetListViewProps): ReactElement {
  const motionPresets = useMotionPresets();
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  return (
    <FramedBox
      meta={{
        id: 'presets.root',
        page: 'presets',
        role: 'container',
        label: '联系人页面容器',
        layout: { ...PAGE_BOX },
        zIndex: 2,
        notes: '坐标含顶部 24dp 状态栏偏移；安卓用 Fragment 根 View',
      }}
      className="zs-page"
      surface="page"
    >
      <FramedBox
        meta={{
          id: 'presets.header',
          page: 'presets',
          role: 'container',
          label: '联系人标题栏',
          layout: { ...LIST_HEADER_BOX },
          zIndex: 5,
          notes: '安卓用 MaterialToolbar',
        }}
        className="zs-header"
      />

      <FramedHeading
        level={1}
        meta={{
          id: 'presets.header.title',
          page: 'presets',
          role: 'text',
          label: '联系人',
          layout: { ...LIST_TITLE_BOX },
          zIndex: 6,
          notes: '页面主标题；安卓用 Toolbar title',
        }}
        className="zs-title"
      >
        联系人
      </FramedHeading>

      <FramedButton
        meta={{
          id: 'presets.communityButton',
          page: 'presets',
          role: 'button',
          label: '栀歆角色卡',
          layout: { ...COMMUNITY_BUTTON_BOX },
          zIndex: 6,
          notes: '角色卡页头部「栀歆角色卡」入口；点击进入 #/community；安卓对应 MaterialButton',
        }}
        className="zs-button zs-button-ghost"
        onClick={onOpenCommunity}
      >
        栀歆角色卡
      </FramedButton>

      <FramedButton
        meta={{
          id: 'presets.createButton',
          page: 'presets',
          role: 'button',
          label: '新建',
          layout: { ...CREATE_BUTTON_BOX },
          zIndex: 6,
          notes: '新建角色卡；安卓用 ExtendedFloatingActionButton',
        }}
        className="zs-button zs-button-primary"
        onClick={() => setCreateMenuOpen((open) => !open)}
      >
        新建⌄
      </FramedButton>

      {createMenuOpen ? (
        <div className="zs-create-menu" data-ui-id="presets.createMenu" data-ui-role="container">
          <FramedButton
            meta={{ id: 'presets.createMenu.newContact', page: 'presets', role: 'button', label: '新建联系人', layout: { ...NEW_CONTACT_MENU_BOX }, zIndex: 18 }}
            className="zs-button zs-button-primary"
            onClick={() => {
              setCreateMenuOpen(false);
              onCreate();
            }}
          >
            新建联系人
          </FramedButton>
          <FramedButton
            meta={{ id: 'presets.importCardButton', page: 'presets', role: 'button', label: '导入角色卡', layout: { ...IMPORT_CARD_BUTTON_BOX }, zIndex: 18, notes: '选择 JSON / PNG / CHARX 角色卡；安卓用 ACTION_OPEN_DOCUMENT' }}
            className="zs-button zs-button-ghost"
            onClick={() => {
              setCreateMenuOpen(false);
              onImportCardClick();
            }}
            disabled={importing}
          >
            导入角色卡
          </FramedButton>
          <FramedButton
            meta={{ id: 'presets.importWorldBookButton', page: 'presets', role: 'button', label: '导入世界书', layout: { ...IMPORT_WORLD_BOOK_BUTTON_BOX }, zIndex: 18, notes: '选择 JSON 文件导入世界书并生成角色卡；安卓用 ACTION_OPEN_DOCUMENT' }}
            className="zs-button zs-button-ghost"
            onClick={() => {
              setCreateMenuOpen(false);
              onImportWorldBookClick();
            }}
            disabled={importing}
          >
            导入世界书
          </FramedButton>
        </div>
      ) : null}

      <FramedMotionBox
        meta={{
          id: 'presets.list',
          page: 'presets',
          role: 'list',
          label: '联系人列表',
          layout: { ...LIST_BOX },
          zIndex: 4,
          notes: '可滚动列表；安卓用 RecyclerView 垂直列表',
        }}
        className="zs-list"
        variants={motionPresets.listStagger.container}
        initial="hidden"
        animate="visible"
      >
        {presets.length === 0 ? (
          <FramedText
            meta={{
              id: 'presets.empty',
              page: 'presets',
              role: 'text',
              label: '空状态提示',
              layout: { ...EMPTY_BOX },
              zIndex: 4,
              notes: '空列表文案；安卓用 TextView',
            }}
            className="zs-empty"
          >
            还没有联系人，点右上角「新建」创建第一位。
          </FramedText>
        ) : null}

        {presets.map((preset, index) => {
          const box = presetItemBoxes(index);
          return (
            <FramedMotionBox
              key={preset.id}
              meta={{
                id: `presets.item.${preset.id}`,
                page: 'presets',
                role: 'container',
              label: `联系人：${preset.name}`,
                layout: { ...box.item },
                zIndex: 4,
                props: ['selected'],
                notes: '列表项卡片；安卓用 MaterialCardView',
              }}
              className="zs-card"
              surface="panel"
              variants={motionPresets.listStagger.item}
            >
              <FramedBox
                meta={{
                  id: `presets.item.${preset.id}.avatar`,
                  page: 'presets',
                  role: 'icon',
                  label: `${preset.name} 头像`,
                  layout: { ...box.avatar },
                  zIndex: 5,
                  notes: '头像占位；安卓用 ShapeableImageView',
                }}
                className="zs-avatar"
              >
                {avatarContent(preset)}
              </FramedBox>

              <FramedText
                meta={{
                  id: `presets.item.${preset.id}.name`,
                  page: 'presets',
                  role: 'text',
                  label: `${preset.name} 名称`,
                  layout: { ...box.name },
                  zIndex: 5,
                  notes: '角色卡名称；安卓用 TextView',
                }}
                className="zs-item-name"
              >
                {preset.name}
              </FramedText>

              <FramedText
                meta={{
                  id: `presets.item.${preset.id}.tags`,
                  page: 'presets',
                  role: 'text',
                  label: `${preset.name} 标签`,
                  layout: { ...box.tags },
                  zIndex: 5,
                  notes: '标签 / 世界书概览；安卓用 TextView',
                }}
                className="zs-item-tags"
              >
                {[...(preset.tags ?? []), worldBookSummary(preset)].join(' · ')}
              </FramedText>

              <FramedText
                meta={{
                  id: `presets.item.${preset.id}.description`,
                  page: 'presets',
                  role: 'text',
                  label: `${preset.name} 简介`,
                  layout: { ...box.description },
                  zIndex: 5,
                  notes: '简介，最多两行；安卓用 TextView + maxLines=2',
                }}
                className="zs-item-description"
              >
                {preset.description ?? ''}
              </FramedText>

              <FramedButton
                meta={{
                  id: `presets.item.${preset.id}.editButton`,
                  page: 'presets',
                  role: 'button',
                  label: `编辑 ${preset.name}`,
                  layout: { ...box.edit },
                  zIndex: 6,
                  notes: '进入角色卡编辑页；安卓用 MaterialButton',
                }}
                className="zs-button zs-button-ghost"
                onClick={() => {
                  onEdit(preset);
                }}
              >
                编辑
              </FramedButton>

              <FramedButton
                meta={{
                  id: `presets.item.${preset.id}.duplicateButton`,
                  page: 'presets',
                  role: 'button',
                  label: `复制 ${preset.name}`,
                  layout: { ...box.duplicate },
                  zIndex: 6,
                  notes: '复制一张角色卡；安卓用 MaterialButton',
                }}
                className="zs-button zs-button-ghost"
                onClick={() => {
                  onDuplicate(preset);
                }}
              >
                复制
              </FramedButton>

              <FramedButton
                meta={{
                  id: `presets.item.${preset.id}.exportButton`,
                  page: 'presets',
                  role: 'button',
                  label: `导出 ${preset.name}`,
                  layout: { ...box.exportJson },
                  zIndex: 6,
                  notes: '导出单张角色卡 JSON；安卓用 SAF CreateDocument',
                }}
                className="zs-button zs-button-ghost"
                onClick={() => {
                  onExport(preset);
                }}
              >
                导出
              </FramedButton>

              <FramedButton
                meta={{
                  id: `presets.item.${preset.id}.deleteButton`,
                  page: 'presets',
                  role: 'button',
                  label: `删除 ${preset.name}`,
                  layout: { ...box.remove },
                  zIndex: 6,
                  notes: '删除（二次确认）；安卓用 MaterialButton + AlertDialog',
                }}
                className="zs-button zs-button-danger"
                onClick={() => {
                  onRequestDelete(preset);
                }}
              >
                删除
              </FramedButton>
            </FramedMotionBox>
          );
        })}
      </FramedMotionBox>

      {importError !== null ? (
        <FramedText
          meta={{
            id: 'presets.notice',
            page: 'presets',
            role: 'text',
            label: '导入 / 导出提示',
            layout: { ...ERROR_BOX },
            zIndex: 30,
            notes: '导入失败提示条；安卓用 Snackbar',
          }}
          className="zs-error zs-import-notice"
          title={importError}
        >
          {importError.split('\n')[0]}
        </FramedText>
      ) : null}

      <AnimatePresence>
        {pendingDelete !== null ? (
          <FramedMotionBox
            key="presets-delete-backdrop"
            meta={{
              id: 'presets.deleteDialog.backdrop',
              page: 'presets',
              role: 'background',
              label: '删除确认遮罩',
              layout: { ...DIALOG_BACKDROP_BOX },
              zIndex: 40,
              notes: '弹层遮罩，点击空白处取消；安卓用 AlertDialog 的 dim',
            }}
            className="zs-backdrop"
            variants={motionPresets.sheetBackdrop}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onCancelDelete}
          />
        ) : null}

        {pendingDelete !== null ? (
          <FramedMotionBox
            key="presets-delete-dialog"
            meta={{
              id: 'presets.deleteDialog',
              page: 'presets',
              role: 'modal',
              label: '删除确认弹窗',
              layout: { ...DIALOG_PANEL_BOX },
              zIndex: 41,
              notes: '删除二次确认；安卓用 MaterialAlertDialog',
            }}
            className="zs-dialog"
            variants={motionPresets.sheet}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <FramedText
              meta={{
                id: 'presets.deleteDialog.message',
                page: 'presets',
                role: 'text',
                label: '删除确认文案',
                layout: { ...DIALOG_MESSAGE_BOX },
                zIndex: 42,
                notes: '确认文案；安卓用 AlertDialog message',
              }}
              className="zs-dialog-message"
            >
              {`确认删除角色卡「${pendingDelete.name}」？此操作不可撤销。`}
            </FramedText>

            <FramedButton
              meta={{
                id: 'presets.deleteDialog.cancelButton',
                page: 'presets',
                role: 'button',
                label: '取消删除',
                layout: { ...DIALOG_CANCEL_BOX },
                zIndex: 42,
                notes: '取消；安卓用 AlertDialog negative button',
              }}
              className="zs-button zs-button-ghost"
              onClick={onCancelDelete}
            >
              取消
            </FramedButton>

            <FramedButton
              meta={{
                id: 'presets.deleteDialog.confirmButton',
                page: 'presets',
                role: 'button',
                label: '确认删除',
                layout: { ...DIALOG_CONFIRM_BOX },
                zIndex: 42,
                notes: '确认删除；安卓用 AlertDialog positive button',
              }}
              className="zs-button zs-button-danger"
              onClick={onConfirmDelete}
            >
              确认删除
            </FramedButton>
          </FramedMotionBox>
        ) : null}
      </AnimatePresence>
    </FramedBox>
  );
}
